import { Link } from "react-router-dom";
import { formatViews, hotFilms } from "@/lib/film";

export default function HotSection({ loading }: { loading: boolean }) {
  const list = hotFilms(7);
  const highlight = list[0];
  const ranks = list.slice(1);

  return (
    <section className="border-t border-background-200 max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 md:mb-8">
        <div>
          <p className="text-[11px] tracking-[0.24em] text-primary-600 uppercase mb-2 font-label">MOST VIEWED</p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">热门推荐</h2>
          <p className="mt-2 text-sm text-foreground-600">根据站内浏览数据排序，了解大家正在关注的作品。</p>
        </div>
        <Link
          to="/category?sort=views"
          className="inline-flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-700 transition-colors whitespace-nowrap"
        >
          热度排行榜
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-4 md:gap-6">
          <div className="w-full aspect-[16/10] rounded-lg skeleton-block"></div>
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-[76px] rounded-lg skeleton-block"></div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-4 md:gap-6">
          {highlight && (
            <Link
              to={`/detail/${highlight.id}`}
              className="group relative rounded-lg overflow-hidden border border-background-200 cursor-pointer"
            >
              <div className="w-full aspect-[16/10] bg-background-200 overflow-hidden">
                <img
                  src={highlight.backdrop ?? highlight.cover}
                  alt={`${highlight.title} 影片剧照`}
                  title={`${highlight.title} 日本电影`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="px-2 py-0.5 rounded-md bg-accent-500 text-accent-950 text-[11px] font-label">
                    热度第一
                  </span>
                  <span className="text-[11px] text-background-200/80 font-label">
                    {formatViews(highlight.views)} 次浏览
                  </span>
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-background-50">{highlight.title}</h3>
                <p className="mt-1.5 text-sm text-background-200/85 line-clamp-2 max-w-[440px]">
                  {highlight.synopsis}
                </p>
                <div className="mt-3 flex items-center gap-3 text-xs text-background-200/75 font-label">
                  <span>{highlight.year}</span>
                  <span className="w-px h-3 bg-background-50/30"></span>
                  <span>{highlight.genres.join(" / ")}</span>
                  <span className="w-px h-3 bg-background-50/30"></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400 w-3.5 h-3.5 flex items-center justify-center"></i>
                    {highlight.rating.toFixed(1)}
                  </span>
                </div>
              </div>
            </Link>
          )}

          <div className="divide-y divide-background-200 border border-background-200 rounded-lg bg-background-50 overflow-hidden">
            {ranks.map((film, index) => (
              <Link
                key={film.id}
                to={`/detail/${film.id}`}
                className="group flex items-center gap-3 md:gap-4 p-3 md:p-3.5 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-6 shrink-0 text-center font-heading text-base text-foreground-400 group-hover:text-primary-600 transition-colors">
                  {String(index + 2).padStart(2, "0")}
                </span>
                <div className="w-11 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={film.cover}
                    alt={`${film.title} 影片封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
                    {film.title}
                  </h3>
                  <p className="mt-0.5 text-xs text-foreground-500 truncate font-label">
                    {film.genres.join(" / ")} · {film.year}
                  </p>
                </div>
                <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xs text-foreground-700 font-label flex items-center gap-1">
                    <i className="ri-fire-line text-accent-600 w-3.5 h-3.5 flex items-center justify-center"></i>
                    {formatViews(film.views)}
                  </span>
                  <span className="text-xs text-foreground-500 font-label flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                    {film.rating.toFixed(1)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}